import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface GalleryImage {
  id: number;
  src: string;
  title: string;
}

interface ProjetoGalleryProps {
  images: GalleryImage[];
}

// ============================================================================
// CONFIGURAÇÕES DE DESIGN SYSTEM
// ============================================================================

const DESIGN_TOKENS = {
  colors: {
    background: '#ffffff',
    text: {
      primary: '#212529',
      secondary: '#6c757d',
    },
    border: {
      default: '#e9ecef',
      hover: '#495057',
    },
    accent: '#6c757d',
    overlay: 'rgba(108, 117, 125, 0.85)',
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    weights: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '4rem',
    xl: '6rem',
  },
  transitions: {
    fast: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    smooth: '1s ease-out',
    bounce: '0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  breakpoints: {
    mobile: '600px',
    tablet: '1024px',
    desktop: '1200px',
  },
} as const;

// ============================================================================
// ANIMAÇÕES
// ============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const drawLine = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const glow = keyframes`
  0%, 100% { 
    box-shadow: 
      0 20px 40px rgba(108, 117, 125, 0.15),
      0 0 60px rgba(108, 117, 125, 0.1);
  }
  50% { 
    box-shadow: 
      0 25px 50px rgba(108, 117, 125, 0.25),
      0 0 80px rgba(108, 117, 125, 0.2);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// ============================================================================
// ESTILOS GLOBAIS
// ============================================================================

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
  
  * {
    box-sizing: border-box;
  }
`;

// ============================================================================
// COMPONENTES ESTILIZADOS
// ============================================================================

const SectionContainer = styled.section`
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  color: ${DESIGN_TOKENS.colors.text.primary};
  min-height: 100vh;
  padding: ${DESIGN_TOKENS.spacing.xl} ${DESIGN_TOKENS.spacing.sm};
  overflow: hidden;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${DESIGN_TOKENS.breakpoints.tablet}) {
    padding: ${DESIGN_TOKENS.spacing.lg} ${DESIGN_TOKENS.spacing.sm};
  }
`;

const BackgroundSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
`;

const AnimatedLine = styled.path<{ delay?: number }>`
  fill: none;
  stroke: ${DESIGN_TOKENS.colors.border.default};
  stroke-width: 1;
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: ${drawLine} 3s ease-out forwards;
  animation-delay: ${({ delay = 0 }) => `${delay}s`};
`;

const SectionTitle = styled.h2`
  font-family: ${DESIGN_TOKENS.typography.fontFamily};
  font-weight: ${DESIGN_TOKENS.typography.weights.bold};
  font-size: clamp(2rem, 5vw, 3.5rem);
  text-align: center;
  margin-bottom: ${DESIGN_TOKENS.spacing.sm};
  z-index: 10;
  position: relative;
  animation: ${fadeIn} ${DESIGN_TOKENS.transitions.smooth} forwards;
  background: linear-gradient(135deg, #212529 0%, #495057 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  font-family: ${DESIGN_TOKENS.typography.fontFamily};
  font-weight: ${DESIGN_TOKENS.typography.weights.regular};
  font-size: 1.0625rem;
  color: ${DESIGN_TOKENS.colors.text.secondary};
  text-align: center;
  max-width: 600px;
  margin-bottom: ${DESIGN_TOKENS.spacing.lg};
  z-index: 10;
  position: relative;
  animation: ${fadeIn} ${DESIGN_TOKENS.transitions.smooth} 0.2s both;
  line-height: 1.8;
  letter-spacing: 0.3px;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${DESIGN_TOKENS.spacing.md};
  width: 100%;
  max-width: ${DESIGN_TOKENS.breakpoints.desktop};
  z-index: 10;
  position: relative;
  animation: ${fadeIn} ${DESIGN_TOKENS.transitions.smooth} 0.3s both;

  @media (max-width: ${DESIGN_TOKENS.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }

  @media (max-width: ${DESIGN_TOKENS.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PhotoCard = styled.div<{ index?: number }>`
  position: relative;
  border: 2px solid ${DESIGN_TOKENS.colors.border.default};
  border-radius: 12px;
  overflow: hidden;
  background-color: ${DESIGN_TOKENS.colors.background};
  transition: all ${DESIGN_TOKENS.transitions.fast};
  cursor: pointer;
  animation: ${slideInUp} ${DESIGN_TOKENS.transitions.smooth} 
    ${({ index = 0 }) => `${0.1 * index}s`} both;

  /* Efeito de borda gradiente ao hover */
  background-image: 
    linear-gradient(${DESIGN_TOKENS.colors.background}, ${DESIGN_TOKENS.colors.background}),
    linear-gradient(135deg, ${DESIGN_TOKENS.colors.accent}, #8e9aaf, ${DESIGN_TOKENS.colors.accent});
  background-origin: border-box;
  background-clip: padding-box, border-box;

  &:hover {
    border-color: ${DESIGN_TOKENS.colors.accent};
    transform: translateY(-12px) scale(1.02);
    box-shadow: 
      0 20px 40px rgba(108, 117, 125, 0.15),
      0 0 60px rgba(108, 117, 125, 0.1);
    animation: ${glow} 2s ease-in-out infinite;
  }

  img {
    width: 100%;
    height: 350px; /* Aumentei para fotos de telefone (mais altas) */
    object-fit: cover;
    object-position: center;
    transition: transform ${DESIGN_TOKENS.transitions.fast};
    display: block;
    position: relative;
    z-index: 2;
  }

  &:hover img {
    transform: scale(1.08) rotate(1deg);
    filter: brightness(0.85);
  }

  &:hover [data-badge] {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, ${DESIGN_TOKENS.colors.accent}, #8e9aaf);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: ${DESIGN_TOKENS.typography.weights.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 4;
  opacity: 0;
  transform: scale(0.8) rotate(-15deg);
  transition: all ${DESIGN_TOKENS.transitions.fast};
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
`;

// ============================================================================
// ESTILOS DO MODAL
// ============================================================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 95%;
  max-height: 95%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalImage = styled.img`
  max-width: 100%;
  max-height: 85vh;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: -20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #333;
  font-size: 2rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    transform: scale(1.1);
  }
`;

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const ProjetoGallery: React.FC<ProjetoGalleryProps> = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  useEffect(() => {
    const fontUrl = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap';

    if (!document.head.querySelector(`link[href="${fontUrl}"]`)) {
      const fontLink = document.createElement('link');
      fontLink.href = fontUrl;
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    }
  }, []);

  const handleImageClick = (image: GalleryImage) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  // Fecha modal com ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [selectedImage]);

  return (
    <>
      <GlobalStyle />
      <SectionContainer id="projetos">
        <BackgroundSVG viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          <AnimatedLine d="M 0 200 L 1200 200" delay={0} />
          <AnimatedLine d="M 0 400 L 1200 400" delay={0.5} />
          <AnimatedLine d="M 0 600 L 1200 600" delay={1} />
          <AnimatedLine d="M 300 0 L 300 800" delay={1.5} />
          <AnimatedLine d="M 900 0 L 900 800" delay={2} />
        </BackgroundSVG>

        <SectionTitle>Galeria de Projetos</SectionTitle>
        <SectionSubtitle>
          Conheça alguns dos projetos executados com excelência pela HN Engenharia
        </SectionSubtitle>

        <GalleryGrid>
          {images.map((img, index) => (
            <PhotoCard key={img.id} index={index} onClick={() => handleImageClick(img)}>
              <Badge data-badge>Projeto</Badge>
              <img src={img.src} alt={img.title} loading="lazy" />
            </PhotoCard>
          ))}
        </GalleryGrid>
      </SectionContainer>

      {selectedImage && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
            <ModalImage src={selectedImage.src} alt={selectedImage.title} />
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default ProjetoGallery;
